import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import PropTypes from 'prop-types';

import { TOAST } from 'constant';
import { toast } from 'layout';
import { mapError } from 'utils/func';
import Loading from 'components/Loading';

const ResearchKpiGroupConfirm = forwardRef(({ isLoading, refetch }, ref) => {
  // #region Data
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [confirmationControl, setConfirmationControl] = useState(null);
  const { mutate, mode, value, setExpandedRows } = useMemo(
    () => ({
      mode: confirmationControl?.mode ?? 'delete',
      mutate: confirmationControl?.mutate ?? null,
      value: confirmationControl?.value ?? { id: null },
      setExpandedRows: confirmationControl?.setExpandedRows ?? null,
    }),
    [confirmationControl]
  );
  // #endregion

  // #region Event
  useImperativeHandle(
    ref,
    () => ({
      open: (_researchKpiGroupControl) => {
        setConfirmationControl({ ..._researchKpiGroupControl });
        setVisible(true);
      },
    }),
    []
  );

  const footerConfirmationSubmit = () => (
    <div>
      <Button
        label={t('researchKpiGroup.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={() => setVisible(false)}
        autoFocus
      />
      <Button
        label={t('researchKpiGroup.ok')}
        className="w-7rem"
        severity="info"
        onClick={() => {
          if (!value?.id) return;
          mutate(value?.id, {
            onSuccess: () => {
              toast(TOAST.SUCCESS, t(`researchKpiGroup.${mode}.success`));
              setVisible(false);
              refetch();
              if (setExpandedRows) setExpandedRows(null);
            },
            onError: (error) => {
              toast(TOAST.ERROR, mapError(t, error));
            },
          });
        }}
      />
    </div>
  );
  // #endregion

  return (
    <Dialog
      header={<p className="text-red-500">{t(`researchKpiGroup.${mode}.title`)}</p>}
      visible={visible}
      position="center"
      style={{ width: '400px' }}
      onHide={() => setVisible(false)}
      draggable={false}
      footer={footerConfirmationSubmit}
    >
      {isLoading && <Loading />}
      <p className="text-lg font-bold">{value?.name}</p>
      <p className="text-lg">{t(`researchKpiGroup.${mode}.confirmationMessage`)}</p>
    </Dialog>
  );
});

export default React.memo(ResearchKpiGroupConfirm);

ResearchKpiGroupConfirm.propTypes = {
  refetch: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

ResearchKpiGroupConfirm.defaultProps = {
  isLoading: false,
};
